import { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart } from '@phosphor-icons/react';
import telegramService from '../services/telegram';
import { useFavorites } from '../context/FavoritesContext';
import CustomBadge from './CustomBadge';
import { trackPlaceEvent } from '../services/api';
import { highlightMatch } from '../utils/highlightText';
import { getPlaceDates } from '../utils/placeDates';

// highlight — подсветка совпадений в названии. Нужна выдаче поиска:
// раньше подсветка была только в автокомплите, а на странице поиска нет,
// хотя ищут в обоих местах одинаково
const PlaceCard = ({ place, showDistance = false, distance = null, highlight = '' }) => {
  const navigate = useNavigate();
  const { isFavorite, toggleFavorite } = useFavorites();

  const handleClick = () => {
    telegramService.hapticFeedback('light');

    // Определяем источник клика.
    // Путь читаем в момент клика, а не через useLocation: иначе каждая
    // карточка подписана на роутер и перерисовывается при любой навигации.
    const pathname = window.location.pathname;
    let source = 'home';
    if (pathname.includes('/category/')) {
      source = 'category';
    } else if (pathname === '/search') {
      source = 'search';
    } else if (pathname === '/nearby') {
      source = 'nearby';
    }

    // Трекаем клик
    trackPlaceEvent(place.id, 'click', source);

    navigate(`/place/${place.slug}`);
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    telegramService.hapticFeedback('light');

    try {
      await toggleFavorite(place.id);
    } catch (error) {
      // Handle favorite toggle error
    }
  };

  // Get image URL
  const getImageUrl = () => {
    if (place.images?.data?.[0]?.url) {
      const url = place.images.data[0].url;
      return url.startsWith('http') ? url : url;
    }
    if (place.images?.[0]?.url) {
      const url = place.images[0].url;
      return url.startsWith('http') ? url : url;
    }
    return 'https://images.unsplash.com/photo-1559925393-8be0ec4767c8?w=400&q=80';
  };

  // Get category name
  const getCategoryName = () => {
    if (place.category?.data?.name) return place.category.data.name;
    if (place.category?.name) return place.category.name;
    return 'Место';
  };

  const isInFavorites = isFavorite(place.id);
  const dates = getPlaceDates(place);

  return (
    <div
      onClick={handleClick}
      className="flex flex-col cursor-pointer"
    >
      {/* Image Container */}
      <div className="relative w-full rounded-lg overflow-hidden" style={{ aspectRatio: '3/4' }}>
        <img
          src={getImageUrl()}
          alt={place.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {/* Favorite Button - Top Right */}
        {/* Без backdrop-filter: размытие пересчитывается каждый кадр и
            роняет плавность горизонтальных слайдеров на Android.
            Фон сделан плотнее, чтобы иконка осталась читаемой.
            Подложка одна на оба состояния: меняется только глиф */}
        <button
          onClick={handleFavoriteClick}
          style={{
            background: 'rgba(0, 0, 0, 0.6)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
          }}
          className="absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-transform active:scale-95"
          aria-label={isInFavorites ? 'Удалить из избранного' : 'Добавить в избранное'}
        >
          {/* Добавленное — залитое и красное: цветом состояние читается
              мгновенно, одним весом разницу на 20px не заметить */}
          <Heart
            size={20}
            weight={isInFavorites ? 'fill' : 'regular'}
            className={isInFavorites ? 'text-theme-favorite-on-photo' : 'text-white'}
          />
        </button>

        {/* Custom Badge - Bottom Left */}
        {place.customLabel && (
          <div className="absolute bottom-3 left-3">
            <CustomBadge label={place.customLabel} />
          </div>
        )}

        {/* Distance Badge - Bottom Right */}
        {showDistance && distance && (
          <div className="absolute bottom-3 right-3">
            <CustomBadge label={distance} />
          </div>
        )}
      </div>

      {/* Text Content */}
      <div className="pt-3">
        {/* Рубрика: caption 13px обычным начертанием — брендбук 5.5.
            Не text-label: он 15px и почти равен названию */}
        <p className="text-caption text-theme-text-secondary mb-1">
          {getCategoryName()}
        </p>
        <h3 className="text-body font-semibold text-theme-text-primary leading-tight line-clamp-2">
          {highlight ? highlightMatch(place.title, highlight) : place.title}
        </h3>
        {/* Сроки — отдельной строкой под названием, а не в рубрике: в узкой
            колонке «Кафе и рестораны · до 30 ноября» переносится, и названия
            соседних карточек встают на разную высоту. Данные — обычным
            весом (брендбук 3.4), цифры табличные (3.5) */}
        {dates?.short && (
          <p className="text-caption text-theme-text-secondary tabular mt-1">
            {dates.short}
          </p>
        )}
      </div>
    </div>
  );
};

// Сравниваем по значимым полям: в слайдерах до 30 карточек, и без memo
// они перерисовываются все разом при любом обновлении родителя
export default memo(PlaceCard, (prev, next) => (
  prev.place?.id === next.place?.id &&
  prev.place?.title === next.place?.title &&
  prev.place?.customLabel === next.place?.customLabel &&
  prev.place?.startDate === next.place?.startDate &&
  prev.place?.endDate === next.place?.endDate &&
  prev.showDistance === next.showDistance &&
  prev.distance === next.distance &&
  // Без этой строки подсветка застревала бы на первом запросе: карточка
  // с тем же id считалась бы неизменной
  prev.highlight === next.highlight
));
